/**
 * Flights Slices Module
 *
 * Re-exports all flights slices and combines them.
 * Organized by template type for clear separation of concerns.
 */
import { combineReducers } from '@reduxjs/toolkit';

// Import reducers
import { templateListingReducer, type TemplateListingState } from './templateListingSlice';
import {
  templateInterstitialReducer,
  type TemplateInterstitialState,
} from './templateInterstitialSlice';
import {
  templateReviewTravellerReducer,
  type TemplateReviewTravellerState,
} from './templateReviewTravellerSlice';
import { addonsReducer, type AddonsState } from './addonsSlice';
import { checkoutReducer, type CheckoutState } from './checkoutSlice';
import { popupsReducer, type FlightsPopupsState } from './popupsSlice';

// ============================================
// Re-export Actions
// ============================================

// Template Listing actions
export {
  setReviewLoading,
  resetTemplateListingState,
  togglePreferenceFilter,
  removePreferenceFilter,
  clearAllPreferenceFilters,
  setPreferenceFilters,
  resetPreferences,
  clearAutosuggestSuggestions,
} from './templateListingSlice';

// Template Interstitial actions
export {
  setSelectedFares,
  setSelectedFareCombo,
  setLoadingFareId,
  resetTemplateInterstitialState,
} from './templateInterstitialSlice';

// Popups actions (centralized for all flights templates)
export {
  setFlightsCurrentPopup,
  setFlightsPopupData,
  showFlightsErrorPopup,
  clearFlightsPopup,
  clearAllFlightsPopups,
  resetFlightsPopupsState,
  API_ERROR_POPUP_KEY,
} from './popupsSlice';

// Template Review Traveller actions (UI State mutations only)
export {
  setTravellerFormValidated,
  setFareSummary,
  setAddonLoading,
  clearReviewPopups,
  setCurrentPopup,
  resetTemplateReviewTravellerState,
  // Saved Travellers Actions (UI State)
  setSavedTravellers,
  clearSavedTravellers,
  toggleSavedTravellerSelection,
  addSavedTraveller,
  updateSavedTraveller,
  markTravellerValidated,
  // Saved Contact Details Actions (UI State)
  setSavedContactDetails,
  updateSavedContactDetailsField,
  clearSavedContactDetails,
  // Saved GST Details Actions (UI State)
  setSavedGstDetails,
  updateSavedGstDetailsField,
  toggleGstOptIn,
  clearSavedGstDetails,
} from './templateReviewTravellerSlice';

// Addons actions
export {
  resetAddonsState,
  clearAddons,
  clearMeals,
  clearBaggage,
  clearCoupons,
  clearSeats,
} from './addonsSlice';

// Checkout actions
export {
  setCheckoutUrl,
  clearCheckoutUrl,
  clearPrebookError,
  resetCheckoutState,
} from './checkoutSlice';

// ============================================
// Re-export Types
// ============================================

export type { TemplateListingState, BitSetFilter } from './templateListingSlice';
export type { TemplateInterstitialState } from './templateInterstitialSlice';
export type {
  TemplateReviewTravellerState,
  ReviewTravellerUiState,
} from './templateReviewTravellerSlice';
export type { AddonsState } from './addonsSlice';
export type { CheckoutState } from './checkoutSlice';
export type { FlightsPopupsState, PopupErrorDetails } from './popupsSlice';

// ============================================
// Combined State Type
// ============================================

export interface FlightsState {
  readonly templateListing: TemplateListingState;
  readonly templateInterstitial: TemplateInterstitialState;
  readonly templateReviewTraveller: TemplateReviewTravellerState;
  readonly addons: AddonsState;
  readonly checkout: CheckoutState;
  readonly popups: FlightsPopupsState;
}

// ============================================
// Combined Reducer
// ============================================

export const flightsReducer = combineReducers({
  templateListing: templateListingReducer,
  templateInterstitial: templateInterstitialReducer,
  templateReviewTraveller: templateReviewTravellerReducer,
  addons: addonsReducer,
  checkout: checkoutReducer,
  popups: popupsReducer,
});
